/**
 * Orka V3 — Priority map (Code Reference §3.2, PRD §6.5).
 *
 * The ONLY priority source of truth. Removes duplicates from TaskCard,
 * BacklogPane, SmartField, and any inline priority logic.
 *
 * Colour layer (Feb 2026): every colour is a CSS variable backed by
 * `design-system/tokens.css`. Light + dark mode are switched at the
 * :root / .dark selectors — NEVER hardcode priority hex anywhere else.
 */
export const PRIORITY_META = Object.freeze({
  none: {
    key: 'none',
    label: 'No priority',
    short: 'None',
    color: 'var(--orka-priority-none)',
    soft: 'var(--orka-priority-none-soft)',
    text: 'var(--orka-priority-none-text)',
    border: 'var(--orka-priority-none-border)',
  },

  emergency: {
    key: 'emergency',
    label: 'P-1 Emergency',
    short: 'P-1',
    color: 'var(--orka-priority-emergency)',
    soft: 'var(--orka-priority-emergency-soft)',
    text: 'var(--orka-priority-emergency-text)',
    border: 'var(--orka-priority-emergency-border)',
  },

  fixed: {
    key: 'fixed',
    label: 'P0 Fixed',
    short: 'P0',
    color: 'var(--orka-priority-fixed)',
    soft: 'var(--orka-priority-fixed-soft)',
    text: 'var(--orka-priority-fixed-text)',
    border: 'var(--orka-priority-fixed-border)',
  },

  high: {
    key: 'high',
    label: 'P1 High',
    short: 'P1',
    color: 'var(--orka-priority-high)',
    soft: 'var(--orka-priority-high-soft)',
    text: 'var(--orka-priority-high-text)',
    border: 'var(--orka-priority-high-border)',
  },

  medium: {
    key: 'medium',
    label: 'P2 Medium',
    short: 'P2',
    color: 'var(--orka-priority-medium)',
    soft: 'var(--orka-priority-medium-soft)',
    text: 'var(--orka-priority-medium-text)',
    border: 'var(--orka-priority-medium-border)',
  },

  low: {
    key: 'low',
    label: 'P3 Low',
    short: 'P3',
    color: 'var(--orka-priority-low)',
    soft: 'var(--orka-priority-low-soft)',
    text: 'var(--orka-priority-low-text)',
    border: 'var(--orka-priority-low-border)',
  },

  later: {
    key: 'later',
    label: 'P4 Later',
    short: 'P4',
    color: 'var(--orka-priority-later)',
    soft: 'var(--orka-priority-later-soft)',
    text: 'var(--orka-priority-later-text)',
    border: 'var(--orka-priority-later-border)',
  },

  someday: {
    key: 'someday',
    label: 'P5 Someday',
    short: 'P5',
    color: 'var(--orka-priority-someday)',
    soft: 'var(--orka-priority-someday-soft)',
    text: 'var(--orka-priority-someday-text)',
    border: 'var(--orka-priority-someday-border)',
  },
});

/** Adapter: legacy numeric priority (-1, 0, 1, 2, 3, 4, 5) → canonical key. */
const NUMERIC_TO_KEY = {
  '-1': 'emergency',
  '0': 'fixed',
  '1': 'high',
  '2': 'medium',
  '3': 'low',
  '4': 'later',
  '5': 'someday',
};

export function getPriorityKey(value) {
  if (value == null || value === '') return 'none';
  if (typeof value === 'number') return NUMERIC_TO_KEY[String(value)] || 'none';
  if (PRIORITY_META[value]) return value;
  return NUMERIC_TO_KEY[String(value)] || 'none';
}

export function getPriority(value) {
  return PRIORITY_META[getPriorityKey(value)];
}

export const PRIORITY_KEYS = Object.freeze(Object.keys(PRIORITY_META));

/** CSS utility class (.orka-priority-*) for the solid fill variant. */
export function priorityClass(value, { soft = false } = {}) {
  const key = getPriorityKey(value);
  return soft ? `orka-priority-${key}-soft` : `orka-priority-${key}`;
}
